/**
 * Person drawer (ENTITY_WORKBENCH_PLAN.md W8).
 *
 * Everything held about one contact, keyed on lower(email) (R16) — and
 * deliberately cross-entity: opening Nicholas Read shows every company he
 * appears for, with his role and approval state at each. Temporal by
 * construction: the sighting ledger and the send log ARE the history.
 *
 * Cynapse is the person record (R15). What it resolves is shown as the
 * record, its `likely_duplicates` are shown as ITS merge candidates — we
 * never merge or name-clean here; that is the resolver's job, not a
 * presentation leg's.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Building2, FileText, Mail, Pencil, Send, ShieldAlert, User, X,
} from 'lucide-react';
import {
  getPersonDetail, setPersonApproval, updatePerson,
  type PersonApprovalAction, type PersonDetail,
} from '../../lib/api/entity-workbench.js';
import { Modal, ModalBody, ModalHeader } from '../ui/modal.js';
import { ApprovalControl } from './ContactsTab.js';

const ROLES = [
  'media_contact', 'pr_representative', 'investor_relations',
  'analyst', 'executive', 'board_member', 'advisor',
];

function fmt(d: string | null | undefined, pattern = 'd MMM yyyy') {
  return d ? format(new Date(d), pattern) : '—';
}

export function PersonDrawer({ email, contextEntityId, onClose, onChanged }: {
  email: string;
  /** The entity whose workbench opened this drawer — its row is marked. */
  contextEntityId?: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const personQuery = useQuery({
    queryKey: ['person-detail', email],
    queryFn: () => getPersonDetail(email),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['person-detail', email] });
    onChanged?.();
  };

  const person = personQuery.data;

  return (
    <Modal onClose={onClose} size="3xl" closeOnBackdrop>
      <ModalHeader>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-grey-900 truncate">
            {person?.profile.full_name ?? email}
          </h2>
          <p className="text-xs text-grey-500 font-mono truncate">{email}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {person && (
            <button
              onClick={() => setEditing(e => !e)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-grey-600 hover:bg-grey-50"
            >
              <Pencil className="h-3.5 w-3.5" /> {editing ? 'Cancel' : 'Edit'}
            </button>
          )}
          <button onClick={onClose} className="text-grey-400 hover:text-grey-600 touch-target">
            <X className="h-5 w-5" />
          </button>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-4">
        {personQuery.isLoading && (
          <p className="py-12 text-center text-sm text-grey-500">Loading contact…</p>
        )}
        {personQuery.isError && (
          <p className="py-12 text-center text-sm text-red-600">Could not load this contact.</p>
        )}

        {person && (
          <>
            {editing ? (
              <EditPersonForm
                person={person}
                onDone={() => { setEditing(false); invalidate(); }}
              />
            ) : (
              <IdentityBlock person={person} />
            )}

            {person.suppression && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Suppressed{person.suppression.scope ? ` (${person.suppression.scope})` : ''} —{' '}
                  {person.suppression.reason ?? 'no reason recorded'} ·{' '}
                  {fmt(person.suppression.suppressed_at)}
                  {person.suppression.suppressed_by ? ` · ${person.suppression.suppressed_by}` : ''}
                  <span className="block text-xs">
                    No notification will send to this address regardless of approval.
                  </span>
                </span>
              </div>
            )}

            <EntitiesBlock
              person={person}
              contextEntityId={contextEntityId}
              onChanged={invalidate}
            />
            <AppearancesBlock person={person} />
            <SendsBlock person={person} />
          </>
        )}
      </ModalBody>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function IdentityBlock({ person }: { person: PersonDetail }) {
  const cyn = person.cynapse;
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="border border-grey-100 rounded-xl p-4">
        <h3 className="text-xs font-semibold text-grey-500 uppercase mb-2 flex items-center gap-1.5">
          <User className="h-3.5 w-3.5" /> As we have seen them
        </h3>
        <dl className="text-sm space-y-1">
          <Row label="Name" value={person.profile.full_name} />
          <Row label="Title" value={person.profile.job_title} />
          <Row label="Organisation" value={person.profile.organization} />
          <Row label="Phone" value={person.profile.phone} />
          <Row
            label="Seen"
            value={`${fmt(person.profile.first_seen)} → ${fmt(person.profile.last_seen)} · ${person.profile.sightings} sighting${person.profile.sightings === 1 ? '' : 's'}`}
          />
        </dl>
        {person.profile.name_variants.length > 1 && (
          <p className="mt-2 text-xs text-grey-400">
            also seen as: {person.profile.name_variants.filter(n => n !== person.profile.full_name).join(' · ')}
          </p>
        )}
        {person.profile.org_variants.length > 1 && (
          <p className="mt-1 text-xs text-grey-400">
            orgs: {person.profile.org_variants.join(' · ')}
          </p>
        )}
      </div>

      <div className="border border-grey-100 rounded-xl p-4">
        <h3 className="text-xs font-semibold text-grey-500 uppercase mb-2 flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5" /> The record
          <span className="font-normal normal-case text-grey-400">— Cynapse</span>
        </h3>
        {cyn.available ? (
          <>
            <dl className="text-sm space-y-1">
              <Row label="Canonical name" value={cyn.full_name} />
              <Row label="Employer" value={cyn.current_employer} />
              <Row label="Title" value={cyn.job_title} />
              <Row label="Matched by" value={cyn.match_method} />
            </dl>
            {(cyn.email_addresses?.length ?? 0) > 0 && (
              <p className="mt-2 text-xs text-grey-500 break-all">
                addresses: {cyn.email_addresses!.map(e => e.email).join(' · ')}
              </p>
            )}
            {(cyn.likely_duplicates?.length ?? 0) > 0 && (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                {cyn.likely_duplicates!.length} likely duplicate
                {cyn.likely_duplicates!.length === 1 ? '' : 's'} in the record —{' '}
                {cyn.likely_duplicates!.map(d => d.canonical_name ?? d.entity_id).join(', ')}.
                <Link to="/entities/merge-review" className="underline ml-1">
                  Resolve in merge review
                </Link>
                <span className="block text-grey-500 mt-0.5">
                  Merging is the resolver's job — this screen never dedups.
                </span>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-grey-400">
            Not resolved to a Cynapse contact yet — edits will create the record.
          </p>
        )}
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-2 min-w-0">
      <dt className="shrink-0 w-28 text-xs text-grey-400 pt-0.5">{label}</dt>
      <dd className="text-sm text-grey-800 min-w-0 break-words">
        {value || <span className="text-grey-300">—</span>}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

function EntitiesBlock({ person, contextEntityId, onChanged }: {
  person: PersonDetail;
  contextEntityId?: string;
  onChanged: () => void;
}) {
  return (
    <section className="border border-grey-100 rounded-xl p-4">
      <h3 className="text-xs font-semibold text-grey-500 uppercase mb-2 flex items-center gap-1.5">
        <Building2 className="h-3.5 w-3.5" />
        Companies they appear for
        <span className="font-normal normal-case text-grey-400">
          — {person.entities.length}
        </span>
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-grey-100 text-left text-xs text-grey-500">
              <th className="py-1.5 pr-3">Company</th>
              <th className="py-1.5 pr-3">Role</th>
              <th className="py-1.5 pr-3">Filings</th>
              <th className="py-1.5 pr-3">Seen</th>
              <th className="py-1.5">Notify</th>
            </tr>
          </thead>
          <tbody>
            {person.entities.map(e => (
              <EntityRow
                key={e.entity_id}
                entity={e}
                email={person.email}
                isContext={e.entity_id === contextEntityId}
                onChanged={onChanged}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EntityRow({ entity, email, isContext, onChanged }: {
  entity: PersonDetail['entities'][number];
  email: string;
  isContext: boolean;
  onChanged: () => void;
}) {
  const approve = useMutation({
    mutationFn: (action: PersonApprovalAction) =>
      setPersonApproval(entity.entity_id, { email, action }),
    onSuccess: onChanged,
  });

  return (
    <tr className={`border-b border-grey-50 ${isContext ? 'bg-primary-50/40' : ''}`}>
      <td className="py-1.5 pr-3 min-w-0">
        <Link
          to={`/entities/${entity.entity_id}?tab=contacts`}
          className="text-grey-900 hover:text-primary-700 hover:underline"
        >
          <span className="font-mono font-semibold">{entity.ticker}</span>{' '}
          <span className="text-grey-600">{entity.display_name}</span>
        </Link>
      </td>
      <td className="py-1.5 pr-3 text-xs text-grey-600">{entity.role ?? '—'}</td>
      <td className="py-1.5 pr-3 text-xs text-grey-600">{entity.announcements}</td>
      <td className="py-1.5 pr-3 text-xs text-grey-500">
        {fmt(entity.first_seen, 'd MMM yy')} → {fmt(entity.last_seen, 'd MMM yy')}
      </td>
      <td className="py-1.5">
        <ApprovalControl
          status={entity.approval_status ?? 'pending'}
          busy={approve.isPending}
          onAction={a => approve.mutate(a)}
        />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------

function AppearancesBlock({ person }: { person: PersonDetail }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? person.appearances : person.appearances.slice(0, 8);

  return (
    <section className="border border-grey-100 rounded-xl p-4">
      <h3 className="text-xs font-semibold text-grey-500 uppercase mb-2 flex items-center gap-1.5">
        <FileText className="h-3.5 w-3.5" />
        Announcement trail
        <span className="font-normal normal-case text-grey-400">
          — {person.appearances.length}
        </span>
      </h3>
      {person.appearances.length === 0 ? (
        <p className="text-sm text-grey-400">No filings held.</p>
      ) : (
        <ul className="space-y-1.5">
          {shown.map(a => (
            <li key={a.announcement_id} className="text-sm min-w-0">
              <Link
                to={`/announcements/${a.announcement_id}`}
                className="text-grey-800 hover:text-primary-700 hover:underline line-clamp-1"
              >
                {a.title ?? '(untitled)'}
              </Link>
              <span className="text-xs text-grey-400">
                {a.ticker ? `${a.ticker} · ` : ''}{fmt(a.date)}
                {a.role ? ` · ${a.role}` : ''}
                {a.organization ? ` · ${a.organization}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {person.appearances.length > 8 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-2 text-xs font-medium text-primary-600 hover:underline"
        >
          {expanded ? 'Show fewer' : `Show all ${person.appearances.length}`}
        </button>
      )}
    </section>
  );
}

function SendsBlock({ person }: { person: PersonDetail }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? person.sends : person.sends.slice(0, 8);

  return (
    <section className="border border-grey-100 rounded-xl p-4">
      <h3 className="text-xs font-semibold text-grey-500 uppercase mb-2 flex items-center gap-1.5">
        <Send className="h-3.5 w-3.5" />
        What we have sent them
        <span className="font-normal normal-case text-grey-400">
          — {person.sent_total} sent · {person.sends.length} attempt{person.sends.length === 1 ? '' : 's'} logged
        </span>
      </h3>
      {person.sends.length === 0 ? (
        <p className="text-sm text-grey-400">Never emailed.</p>
      ) : (
        <ul className="space-y-1.5">
          {shown.map(s => (
            <li key={s.notification_id} className="text-sm min-w-0">
              <span className="text-grey-800 line-clamp-1">{s.article_title ?? '(article removed)'}</span>
              <span className="text-xs text-grey-400">
                {s.ticker ? `${s.ticker} · ` : ''}{fmt(s.sent_at, 'd MMM yyyy HH:mm')}
                {' · '}{s.status}
                {s.delivery_status ? ` · ${s.delivery_status}` : ''}
                {s.reason ? ` · ${s.reason}` : ''}
                {s.target ? ` · ${s.target}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {person.sends.length > 8 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-2 text-xs font-medium text-primary-600 hover:underline"
        >
          {expanded ? 'Show fewer' : `Show all ${person.sends.length}`}
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function EditPersonForm({ person, onDone }: { person: PersonDetail; onDone: () => void }) {
  const [fullName, setFullName] = useState(person.profile.full_name ?? '');
  const [jobTitle, setJobTitle] = useState(person.profile.job_title ?? '');
  const [org, setOrg] = useState(person.profile.organization ?? '');
  const [phone, setPhone] = useState(person.profile.phone ?? '');
  const [role, setRole] = useState('media_contact');

  useEffect(() => {
    setFullName(person.profile.full_name ?? '');
    setJobTitle(person.profile.job_title ?? '');
    setOrg(person.profile.organization ?? '');
    setPhone(person.profile.phone ?? '');
  }, [person.email]);

  const save = useMutation({
    mutationFn: () => updatePerson({
      email: person.email,
      full_name: fullName.trim(),
      job_title: jobTitle.trim() || null,
      organisation: org.trim() || null,
      phone: phone.trim() || null,
      role,
      cynapse_entity_id: person.cynapse.entity_id ?? null,
    }),
    onSuccess: onDone,
  });

  return (
    <section className="border border-primary-200 bg-primary-50/30 rounded-xl p-4 space-y-3">
      <p className="text-xs text-grey-500">
        Corrections write to the Cynapse contact record — the single source of truth for
        people. Assayer keeps no person record of its own.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Full name">
          <input
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Job title">
          <input
            value={jobTitle}
            onChange={e => setJobTitle(e.target.value)}
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Organisation">
          <input
            value={org}
            onChange={e => setOrg(e.target.value)}
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Phone">
          <input
            value={phone}
            onChange={e => setPhone(e.target.value)}
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Role">
          <select
            value={role}
            onChange={e => setRole(e.target.value)}
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm bg-surface"
          >
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Email">
          <input
            value={person.email}
            disabled
            title="The email is the key — add a new address via the notification draft screen."
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm bg-grey-50 text-grey-500 font-mono"
          />
        </Field>
      </div>
      <div className="flex items-center justify-end gap-2">
        {save.isError && (
          <span className="text-xs text-red-600 mr-auto">
            {(save.error as any)?.response?.data?.detail ?? 'Save failed — try again.'}
          </span>
        )}
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !fullName.trim()}
          className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save to record'}
        </button>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-grey-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
